import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

/**
 * IconContainer Types
 */
export type IconContainerSize = 32 | 36 | 44 | 52 | 72 | 96;

export type IconContainerColor =
  | 'primary'
  | 'neutral'
  | 'info'
  | 'success'
  | 'warning'
  | 'error'
  | 'teal'
  | 'purple'
  | 'sky'
  | 'cyan'
  | 'indigo'
  | 'emerald'
  | 'sapgreen'
  | 'burntorange'
  | 'brickred'
  | 'accessred'
  | 'electricviolet'
  | 'blueviolet';
export type IconContainerColorMode = 'default' | 'inverted';

/**
 * IconContainer Border Radius Types
 */
export type IconContainerBorderRadius = 'rounded' | 'squared';

/**
 * IconContainer Variants
 */
const iconContainerVariants = cva(
  'inline-flex items-center justify-center shrink-0 transition-colors [&>*]:shrink-0',
  {
    variants: {
      size: {
        32: 'h-8 w-8 [&>*]:size-4',
        36: 'h-9 w-9 [&>*]:size-4.5',
        44: 'h-11 w-11 [&>*]:size-5.5',
        52: 'h-[3.25rem] w-[3.25rem] [&>*]:size-7',
        72: 'h-[4.5rem] w-[4.5rem] [&>*]:size-10',
        96: 'h-24 w-24 [&>*]:size-12',
      },
      useProductIcon: {
        false: '',
        true: 'bg-transparent text-transparent border-none shadow-none',
      },
      borderRadius: {
        rounded: 'rounded-full',
        squared: '',
      },
      color: {
        // Primary
        primary: 'bg-primary text-primary-foreground',

        // Functional Colors (EDS Order)
        neutral: 'bg-muted text-muted-foreground',
        info: 'bg-info-100 text-info-700',
        success: 'bg-success-100 text-success-700',
        warning: 'bg-warning-100 text-warning-700',
        error: 'bg-error-100 text-error-700',

        // EDS Colors (Following EDS Style Order)
        teal: 'bg-teal-50 text-teal-700',
        purple: 'bg-purple-50 text-purple-700',
        sky: 'bg-sky-50 text-sky-700',
        cyan: 'bg-cyan-50 text-cyan-700',
        indigo: 'bg-indigo-50 text-indigo-700',
        emerald: 'bg-emerald-50 text-emerald-700',
        sapgreen: 'bg-sapgreen-50 text-sapgreen-700',
        burntorange: 'bg-burntorange-50 text-burntorange-700',
        brickred: 'bg-brickred-50 text-brickred-700',
        accessred: 'bg-accessred-50 text-accessred-700',
        electricviolet: 'bg-electricviolet-50 text-electricviolet-700',
        blueviolet: 'bg-blueviolet-50 text-blueviolet-700',
      },
      colorMode: {
        default: '',
        inverted: '',
      },
    },
    compoundVariants: [
      // Inverted variants for primary
      {
        color: 'primary',
        colorMode: 'inverted',
        class: 'bg-primary-foreground text-primary border border-primary/20',
      },
      // Inverted variants for functional colors
      {
        color: 'neutral',
        colorMode: 'inverted',
        class: 'bg-muted-foreground text-muted',
      },
      {
        color: 'info',
        colorMode: 'inverted',
        class: 'bg-info-700 text-info-100',
      },
      {
        color: 'success',
        colorMode: 'inverted',
        class: 'bg-success-700 text-success-100',
      },
      {
        color: 'warning',
        colorMode: 'inverted',
        class: 'bg-warning-700 text-warning-100',
      },
      {
        color: 'error',
        colorMode: 'inverted',
        class: 'bg-error-700 text-error-100',
      },
      // Inverted variants for EDS colors (Following EDS Style Order)
      {
        color: 'teal',
        colorMode: 'inverted',
        class: 'bg-teal-700 text-teal-100',
      },
      {
        color: 'purple',
        colorMode: 'inverted',
        class: 'bg-purple-700 text-purple-100',
      },
      {
        color: 'sky',
        colorMode: 'inverted',
        class: 'bg-sky-700 text-sky-100',
      },
      {
        color: 'cyan',
        colorMode: 'inverted',
        class: 'bg-cyan-700 text-cyan-100',
      },
      {
        color: 'indigo',
        colorMode: 'inverted',
        class: 'bg-indigo-700 text-indigo-100',
      },
      {
        color: 'emerald',
        colorMode: 'inverted',
        class: 'bg-emerald-700 text-emerald-100',
      },
      {
        color: 'sapgreen',
        colorMode: 'inverted',
        class: 'bg-sapgreen-700 text-sapgreen-100',
      },
      {
        color: 'burntorange',
        colorMode: 'inverted',
        class: 'bg-burntorange-700 text-burntorange-100',
      },
      {
        color: 'brickred',
        colorMode: 'inverted',
        class: 'bg-brickred-700 text-brickred-100',
      },
      {
        color: 'accessred',
        colorMode: 'inverted',
        class: 'bg-accessred-700 text-accessred-100',
      },
      {
        color: 'electricviolet',
        colorMode: 'inverted',
        class: 'bg-electricviolet-700 text-electricviolet-100',
      },
      {
        color: 'blueviolet',
        colorMode: 'inverted',
        class: 'bg-blueviolet-700 text-blueviolet-100',
      },
      // Product Icon variants - override all colors when useProductIcon is true
      {
        useProductIcon: true,
        class: '!bg-transparent !text-transparent !border-transparent !shadow-none',
      },
      // Squared border radius variants based on size
      {
        size: 32,
        borderRadius: 'squared',
        class: 'rounded-[0.5rem]',
      },
      {
        size: 36,
        borderRadius: 'squared',
        class: 'rounded-[0.5rem]',
      },
      {
        size: 44,
        borderRadius: 'squared',
        class: 'rounded-[0.5rem]',
      },
      {
        size: 52,
        borderRadius: 'squared',
        class: 'rounded-[0.625rem]',
      },
      {
        size: 72,
        borderRadius: 'squared',
        class: 'rounded-[0.75rem]',
      },
      {
        size: 96,
        borderRadius: 'squared',
        class: 'rounded-[0.75rem]',
      },
    ],
    defaultVariants: {
      size: 36,
      borderRadius: 'rounded',
      color: 'primary',
      colorMode: 'default',
      useProductIcon: false,
    },
  },
);

/**
 * IconContainer Props Interface
 */
export interface IconContainerProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof iconContainerVariants> {
  /** Icon element to display inside the container */
  children: React.ReactNode;
  /** Product icon element (SVG/PNG) to display instead of regular icon */
  productIcon?: React.ReactNode;
  /** Toggle between regular icon (false) and product icon (true) */
  useProductIcon?: boolean;
  /** Size of the container in pixels */
  size?: IconContainerSize;
  /** Border radius style - rounded (circular) or squared (size-specific radius) */
  borderRadius?: IconContainerBorderRadius;
  /** Color theme */
  color?: IconContainerColor;
  /** Color mode - default or inverted */
  colorMode?: IconContainerColorMode;
}

/**
 * IconContainer Component
 *
 * A flexible container component for displaying icons with various size, color,
 * and styling options. Supports both default and inverted color modes across
 * primary, functional, and accent color palettes.
 *
 * Icon sizes are automatically set based on container size:
 * - 32px/36px containers → 20px icons
 * - 44px/52px containers → 28px icons
 * - 72px containers → 48px icons
 * - 96px containers → 64px icons
 *
 * Border radius options:
 * - "rounded": All sizes use rounded-full (100% circular)
 * - "squared": Size-specific border radius (32/36/44→0.5rem, 52→0.625rem, 72/96→0.75rem)
 *
 * Product Icon Mode:
 * - When useProductIcon={true}, the container surface styling is fully disabled
 * - Background, text color, border and shadow are removed
 * - color / colorMode props are ignored intentionally
 * - If productIcon is not provided, children are rendered as a fallback
 *
 * Neutral Color Note:
 * - The neutral variant intentionally uses bg-muted / text-muted-foreground for better contrast pairing
 *   rather than neutral-* scale tokens. This aligns with EDS surface/text neutrality. Inverted flips these.
 *
 * @example
 * Regular icon usage:
 * ```tsx
 * import { IconContainer } from "@/components/blocks/icon-container"
 * import { Home } from "lucide-react"
 *
 * <IconContainer size={44} borderRadius="rounded" color="primary">
 *   <Home />
 * </IconContainer>
 * ```
 *
 * Product icon usage:
 * ```tsx
 * <IconContainer
 *   size={44}
 *   borderRadius="squared"
 *   color="primary"
 *   useProductIcon
 *   productIcon={<img src="/logo.svg" alt="Product" className="h-full w-full object-cover" />}
 * >
 *   <Home />
 * </IconContainer>
 * ```
 */
const IconContainer = React.forwardRef<HTMLDivElement, IconContainerProps>(
  (
    {
      children,
      productIcon,
      useProductIcon = false,
      className,
      size = 36,
      borderRadius,
      color = 'primary',
      colorMode = 'default',
      ...props
    },
    ref,
  ) => {
    const resolvedBorderRadius = borderRadius ?? 'rounded';

    // Determine what to render: product icon or regular icon
    const iconContent = useProductIcon && productIcon ? productIcon : children;

    return (
      <div
        ref={ref}
        className={cn(
          iconContainerVariants({
            size,
            borderRadius: resolvedBorderRadius,
            color: useProductIcon ? undefined : color,
            colorMode: useProductIcon ? undefined : colorMode,
            useProductIcon,
          }),
          className,
        )}
        {...props}
      >
        {iconContent}
      </div>
    );
  },
);

IconContainer.displayName = 'IconContainer';

export { IconContainer };

export default IconContainer;
